import getters from './getters';
import mutations from './mutations';
import actions from './actions';
import {Tag} from '@/profile-creation-wizard/components/for-creation/Tag';
import {ProfileFamily} from '@/common/api/api-raw-types';
import {FamiliesCategoriesEdition} from '@/profile-creation-wizard/common/categories/FamiliesCategories';
import {
    Address,
    ExperienceLevel,
    FunctionalSkill,
    IndustrySkill,
    Language,
    ProfileCreationForm,
    StrategyBackground,
    WorkplacePreference,
    defaultAddress,
    PriceConfiguration,
} from '@/profile-creation-wizard/types';

const defaultForm: ProfileCreationForm = {
    familiesCategoriesEdition: new FamiliesCategoriesEdition(),
    headline: null,
    experienceLevel: null,
    tags: [],
    industryExpertises: [],
    functionalSkills: [],
    price: 0,
    priceHide: false,
    address: defaultAddress,
    workPlacePreference: null,
    willingnessToTravelInternationally: false,
    languages: [],
    phone: null,
    invitationCode: null,
    groupInvitationId: null,
};

export default {
    state: {
        form: defaultForm,
        familiesDefinition: [] as ProfileFamily[],
        qrcode: {
            url: '',
        },
        priceConfiguration: {
            minPriceAllowed: 25,
            maxPriceAllowed: 5000,
        },
        strategyBackground: {
            managementConsultant: false,
            interimExecutive: false,
        },
    },
    mutations: {
        [mutations.updateHeadline]: (state, headline: string) => {
            state.form.headline = headline;
        },
        [mutations.loadFamiliesDefinition]: (state, families: ProfileFamily[]) => {
            state.familiesDefinition = families;
        },
        [mutations.selectManualFamily]: (state, family: string) => {
            state.form.familiesCategoriesEdition.manual.selectFamily(family);
        },
        [mutations.unselectPredictionFamilyAndCategory]: (state, familyAndCategory: {family: string; category: string}) => {
            state.form.familiesCategoriesEdition.predicted.unSelectCategory(
                familyAndCategory.family,
                familyAndCategory.category,
                state.familiesDefinition,
            );
        },
        [mutations.selectPredictionFamilyAndCategory]: (state, familyAndCategory: {family: string; category: string}) => {
            state.form.familiesCategoriesEdition.predicted.selectCategory(familyAndCategory.family, familyAndCategory.category);
        },
        [mutations.selectManualCategories]: (state, categories: Array<string>) => {
            state.form.familiesCategoriesEdition.manual.selectCategories(categories);
        },
        [mutations.unselectManualCategories]: (state) => {
            state.form.familiesCategoriesEdition.manual.resetSelection();
        },
        [mutations.selectManualSuggestedCategory]: (state, suggestedCategory: string) => {
            state.form.familiesCategoriesEdition.manual.selectOtherCategory(suggestedCategory);
        },
        [mutations.switchToCategoryPrediction]: (state) => {
            state.form.familiesCategoriesEdition.switchToPrediction();
        },
        [mutations.switchToCategoryManual]: (state) => {
            state.form.familiesCategoriesEdition.switchToManual();
        },
        [mutations.clearFamiliesAndCategories]: (state) => {
            state.form.familiesCategoriesEdition.resetAll();
        },
        [mutations.selectAddress]: (state, address: Address) => {
            state.form.address = address;
        },
        [mutations.selectExperienceLevel]: (state, experienceLevel: ExperienceLevel) => {
            state.form.experienceLevel = experienceLevel;
        },
        [mutations.unselectExperienceLevel]: (state) => {
            state.form.experienceLevel = null;
        },
        [mutations.selectPrice]: (state, price: number) => {
            state.form.price = price;
        },
        [mutations.selectPriceHide]: (state, hide: boolean) => {
            state.form.priceHide = hide;
        },
        [mutations.selectTag]: (state, tag: Tag) => {
            state.form.tags.push(tag);
        },
        [mutations.selectPhone]: (state, phone: string) => {
            state.form.phone = phone;
        },
        [mutations.removePhone]: (state) => {
            state.form.phone = null;
        },
        [mutations.selectWorkplacePreference]: (state, workPlacePreference: WorkplacePreference) => {
            state.form.workPlacePreference = workPlacePreference;
        },
        [mutations.unselectTag]: (state, tag: Tag) => {
            state.form.tags = state.form.tags.filter((t: Tag) => t.name !== tag.name);
        },
        [mutations.selectInvitationCode]: (state, invitationCode: string) => {
            state.form.invitationCode = invitationCode;
        },
        [mutations.selectGroupInvitationId]: (state, groupInvitationId: string) => {
            state.form.groupInvitationId = groupInvitationId;
        },
        [mutations.resetDetails]: (state) => {
            state.form.headline = null;
            state.form.address = defaultAddress;
            state.form.experienceLevel = null;
            state.form.price = 0;
            state.form.phone = null;
            state.form.tags = [];
        },
        [mutations.resetDetailsInPredictor]: (state) => {
            state.form.address = defaultAddress;
            state.form.price = 0;
            state.form.phone = null;
            state.form.tags = [];
        },
        [mutations.setupQrcode]: (state, qrcodeUrl: string) => {
            state.qrcode.url = qrcodeUrl;
        },
        [mutations.changeLanguages]: (state, languages: Language[]) => {
            state.form.languages = languages;
        },
        [mutations.updateIndustryExpertises]: (state, expertises: IndustrySkill[]) => {
            state.form.industryExpertises = expertises;
        },
        [mutations.updateFunctionalSkills]: (state, functionalSkills: FunctionalSkill[]) => {
            state.form.functionalSkills = functionalSkills;
        },
        [mutations.updateWillingnessToTravelInternationally]: (state, willingnessToTravelInternationally: boolean) => {
            state.form.willingnessToTravelInternationally = willingnessToTravelInternationally;
        },
        [mutations.updateStrategyBackground]: (state, options: StrategyBackground) => {
            state.strategyBackground = options;
        },
        [mutations.definePriceConfiguration]: (state, priceConfig: PriceConfiguration) => {
            state.priceConfiguration = priceConfig;
        },
    },
    getters: {
        [getters.creationForm]: (state) => {
            return state.form;
        },
        [getters.allFamiliesSelectable]: (state) => {
            return state.familiesDefinition;
        },
        [getters.qrcodeSetup]: (state) => {
            return state.qrcode;
        },
        [getters.selectedCategories]: (state) => {
            return state.form.familiesCategoriesEdition.allSelectedCategories();
        },
        [getters.selectedBackground]: (state) => {
            return state.strategyBackground;
        },
        [getters.priceConfiguration]: (state) => {
            return state.priceConfiguration;
        },
    },
    actions: {
        [actions.importManualFamilyCategories]: (context, manualData) => {
            context.commit('SWITCH_TO_CATEGORY_MANUAL');
            context.commit('SELECT_MANUAL_FAMILY', manualData.selectedFamily);
            context.commit('SELECT_MANUAL_CATEGORIES', manualData.selectedCategories);
            context.commit('SELECT_MANUAL_SUGGESTED_CATEGORY', manualData.selectedOtherCategory);
        },
    },
};
